import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Script from 'next/script';

type ApplePaySdkStatus = 'loading' | 'ready' | 'error';

type ApplePayContextValue = {
  isReady: boolean;
  hasError: boolean;
};

const ApplePayContext = createContext<ApplePayContextValue>({
  isReady: false,
  hasError: false,
});

const APPLE_PAY_SDK_SRC =
  'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

const ApplePayProvider = ({ children }: { children?: React.ReactNode }) => {
  const [status, setStatus] = useState<ApplePaySdkStatus>('loading');

  useEffect(() => {
    // Safari exposes ApplePaySession natively, before the SDK script loads.
    if (window.ApplePaySession) {
      setStatus('ready');
    }
  }, []);

  return (
    <ApplePayContext.Provider
      value={useMemo(
        () => ({ isReady: status === 'ready', hasError: status === 'error' }),
        [status],
      )}
    >
      <Script
        crossOrigin="anonymous"
        strategy="afterInteractive"
        src={APPLE_PAY_SDK_SRC}
        onReady={() => setStatus('ready')}
        onError={() => setStatus('error')}
      />
      {children}
    </ApplePayContext.Provider>
  );
};

export default ApplePayProvider;

export const useApplePayContext = () => useContext(ApplePayContext);
